import React, { FC, JSX, useContext, useEffect, useMemo, useState } from "react";
import { Link, useLocation, useRouteMatch } from "react-router-dom";
import { AdequacyProgressBarLabels } from "@app/shared/progressBarBand";
import { ProgressBar } from "@app/shared/ProgressBar";
import { NetworkAssessmentAnalysisType, ProjectPerspective, ProjectRunConfigurationType, TimeFrequency } from "@app/projects/enumerators";
import { querystringify } from "@app/shared/utility/querystringify";
import { CountyProjectContext } from "@app/projects/CountyProjectContext";
import GeographyService from "@app/geography/geographyService";
import ProjectService from "@app/projects/projectService";
import RegionSet from "@app/region-sets/regionSet";
import AdequacyRegionSetAggregate from "@app/projects/results/adequacy/adequacyRegionSetAggregate";
import { ColumnConfig, ColumnGroup, numberSort, printNumberAsString, SortDirections, WafflesFilterType } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import Roles from "@app/user/roles";
import { UserContext } from "@app/user/UserContext";
import { ExportType } from "@app/exports/enumerations";
import { RegionGranularity } from "@app/region-sets";
import { ExportDataRouteState } from "@app/exports/exportsRouteState";
import { ReportCustomizationRouteState } from "@app/report-customization/build-reports/interfaces/reportCustomizationRouteState";
import { ReportType } from "@app/report-customization/build-reports/interfaces/reportTypeEnum";
import { ProviderNetworkTypeEnum } from "@app/shared/enums/providerNetworkTypeEnum";
import { getPrimaryAndSecondaryRunConfigurations, showPatternOfCare } from "@app/projects/network-assessment/adequacy/utility";
import { FeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";

interface RegionAggregateViewProps {
  exportComparedRunConfigurationId?: number;
  exportEndpoint: string;
}

interface RegionAggregateWaffleToppings {
  accessMet: string;
  accessNotMet: string;
  accessTotal: string;
  adequacyGraph: JSX.Element;
  adequacyIndex: string;
  attestationPercent: string;
  countyCount: string;
  exceptionsApproved: string;
  exceptionsTotal: string;
  fullMembershipCount: string;
  gapsFillable: string;
  gapsFillableByMultipleProviders: string;
  gapsFillableBySingleProvider: string;
  gapsTotal: string;
  key: string;
  membershipRisk: string;
  membershipRiskPercent: string;
  servicingMet: string;
  servicingNotMet: string;
  servicingTotal: string;
  title: JSX.Element;
  titleText: string;
}

interface RegionRow {
  aggregate: AdequacyRegionSetAggregate | undefined;
  regionSet: RegionSet;
}

const COLUMN_GROUPS: Array<ColumnGroup<RegionAggregateWaffleToppings>> = [
  { children: ["title", "countyCount", "fullMembershipCount"], hideKey: true, key: "id", testId: "group-id" },
  { children: ["adequacyIndex", "adequacyGraph"], hideKey: true, key: "adequacy", testId: "group-adequacy" },
  { children: ["gapsTotal", "gapsFillable", "gapsFillableBySingleProvider", "gapsFillableByMultipleProviders"], key: "Gaps", testId: "group-gaps" },
  { children: ["exceptionsTotal", "exceptionsApproved"], key: "Exceptions", testId: "group-exceptions" },
  { children: ["accessMet", "accessNotMet", "accessTotal"], key: "Access", testId: "group-access" },
  { children: ["servicingMet", "servicingNotMet", "servicingTotal"], key: "Servicing", testId: "group-servicing" },
  { children: ["membershipRisk", "membershipRiskPercent"], key: "Membership at Risk", testId: "group-membership-risk" },
  { children: ["attestationPercent"], hideKey: true, key: "attestation-region", testId: "group-attestation" },
];

const geographyService = new GeographyService();
const projectService = new ProjectService();

export function buildToppings(rows: RegionRow[], matchUrl: string): RegionAggregateWaffleToppings[] {
  /* oxlint-disable sort-keys */
  return rows.map(({ regionSet, aggregate }) => {
    const regionName = regionSet.regionCode ?? `Region ${regionSet.regionSetId}`;
    return ({
    key: String(regionSet.regionSetId),
    titleText: regionName,
    title: (
      <span>
        <Link
          to={{
            pathname: `${matchUrl}/region/${regionSet.regionSetId}`,
            search: querystringify({ regionName, viewBy: ProjectPerspective.ByCounty }),
          }}
        >
          {regionName}
        </Link>
      </span>
    ),
    countyCount: String(regionSet.partialRegionSets?.length ?? 0),
    fullMembershipCount: printNumberAsString(aggregate?.fullMembershipCount, "---"),
    adequacyIndex: aggregate?.adequacyIndex !== null && aggregate?.adequacyIndex !== undefined
      ? aggregate.adequacyIndex.toFixed(1)
      : "---",
    adequacyGraph: (
      <div style={{ minWidth: 200 }}>
        <ProgressBar
          bands={[
            { color: "progress-bar-success", colorType: "class", label: AdequacyProgressBarLabels.MetBothCriteria, outOfScale: false, percent: aggregate?.percentPassBoth ?? 0 },
            { color: "progress-bar-warning", colorType: "class", label: AdequacyProgressBarLabels.MetOneCriteria, outOfScale: false, percent: (aggregate?.percentPassAccessOnly ?? 0) + (aggregate?.percentPassServicingOnly ?? 0) },
            { color: "progress-bar-danger", colorType: "class", label: AdequacyProgressBarLabels.MetNoCriteria, outOfScale: false, percent: aggregate?.percentPassNone ?? 0 },
            { color: "progress-bar-info", colorType: "class", label: AdequacyProgressBarLabels.ApprovedException, outOfScale: false, percent: aggregate?.percentPassByException ?? 0 },
          ]}
          isGrayscale={false}
        />
      </div>
    ),
    gapsTotal: printNumberAsString(aggregate?.gapCount, "---"),
    gapsFillable: printNumberAsString(aggregate?.fillableCount, "---"),
    gapsFillableBySingleProvider: printNumberAsString(aggregate?.fillableBySingleProviderCount, "---"),
    gapsFillableByMultipleProviders: printNumberAsString(aggregate?.fillableByMultipleProvidersCount, "---"),
    exceptionsTotal: printNumberAsString(aggregate?.exceptionCount, "---"),
    exceptionsApproved: printNumberAsString(aggregate?.approvedExceptionCount, "---"),
    accessMet: printNumberAsString(aggregate?.accessMetCount, "---"),
    accessNotMet: printNumberAsString(aggregate?.accessNotMetCount, "---"),
    accessTotal: printNumberAsString(aggregate?.accessTotalCount, "---"),
    servicingMet: printNumberAsString(aggregate?.servicingMetCount, "---"),
    servicingNotMet: printNumberAsString(aggregate?.servicingNotMetCount, "---"),
    servicingTotal: printNumberAsString(aggregate?.servicingTotalCount, "---"),
    membershipRisk: printNumberAsString(aggregate?.membershipRiskCount, "---"),
    membershipRiskPercent: aggregate?.percentMembershipRisk !== null && aggregate?.percentMembershipRisk !== undefined
      ? aggregate.percentMembershipRisk.toFixed(1)
      : "---",
    attestationPercent: aggregate?.recentlyAttestedUniqueNPIPercent !== null && aggregate?.recentlyAttestedUniqueNPIPercent !== undefined
      ? aggregate.recentlyAttestedUniqueNPIPercent.toFixed(1)
      : "---",
  });
  });
  /* oxlint-enable sort-keys */
}

export function customSort(
  records: RegionAggregateWaffleToppings[],
  column: keyof RegionAggregateWaffleToppings,
  direction: string,
): RegionAggregateWaffleToppings[] {
  const multiplier = direction === SortDirections.Asc ? 1 : -1;
  switch (column) {
    case "title":
      return [...records].sort((a, b) => (a.titleText > b.titleText ? 1 : -1) * multiplier);
    case "adequacyGraph":
      return numberSort<RegionAggregateWaffleToppings>(records, "adequacyIndex", multiplier);
    default:
      return numberSort(records, column, multiplier);
  }
}

const RegionAggregateView: FC<RegionAggregateViewProps> = ({ exportComparedRunConfigurationId, exportEndpoint }): JSX.Element => {
  const {
    adequacyFilters,
    adequacyResult,
    cmsCreditIsApplying,
    compareToRunConfiguration,
    filters,
    isDualMembershipMode,
    module,
    networkAssessmentAnalysisType,
    perspective,
    project,
    runConfiguration,
    runFrequency,
  } = useContext(CountyProjectContext);
  const user = useContext(UserContext);
  const { enabledFeatureFlags } = useContext(FeatureFlagContext);
  const [rows, setRows] = useState<RegionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const location = useLocation();
  const { url: matchUrl } = useRouteMatch();

  useEffect(() => {
    if (!project?.projectId || !runConfiguration?.projectRunConfigurationId) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const regions = await geographyService.getRegions(project.projectId, runConfiguration.projectRunConfigurationId);
        const regionSetIds = regions.map((r) => r.regionSetId).filter((id): id is number => id !== null);
        const aggregates = await projectService.getRegionalAggregates(
          project.projectId,
          runConfiguration.projectRunConfigurationId,
          regionSetIds,
          networkAssessmentAnalysisType ?? NetworkAssessmentAnalysisType.ServiceArea
        );
        if (!cancelled) {
          setRows(regions.map((regionSet) => ({
            aggregate: aggregates.find((a) => a.regionSetId === regionSet.regionSetId),
            regionSet,
          })));
        }
      } catch {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [project?.projectId, runConfiguration?.projectRunConfigurationId, networkAssessmentAnalysisType]);

  const userHasEnhancedFillableGaps = user?.roles?.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER) ?? false;
  const userHasMembershipRisk = user?.roles?.includes(Roles.MEMBERSHIP_RISK_USER) ?? false;

  const columnConfig: ColumnConfig = useMemo(() => ({
    accessMet: { displayText: "Met", filterText: "Access: Met", filterType: WafflesFilterType.number, testId: "access-met" },
    accessNotMet: { displayText: "Not Met", filterText: "Access: Not Met", filterType: WafflesFilterType.number, testId: "access-not-met" },
    accessTotal: { displayText: "Total", filterText: "Access: Total", filterType: WafflesFilterType.number, testId: "access-total" },
    adequacyGraph: { displayText: "Adequacy", minWidth: "200px", noFilter: true, testId: "adequacy-graph" },
    adequacyIndex: { displayText: "Adequacy Index", filterType: WafflesFilterType.number, testId: "adequacy-index" },
    attestationPercent: { displayText: "Attestation %", filterType: WafflesFilterType.number, hidden: !project?.showAttestation, testId: "attestation-percent" },
    countyCount: { displayText: "Counties", filterType: WafflesFilterType.number, testId: "county-count" },
    exceptionsApproved: { displayText: "Approved", filterText: "Exceptions: Approved", filterType: WafflesFilterType.number, testId: "exceptions-approved" },
    exceptionsTotal: { displayText: "Total", filterText: "Exceptions: Total", filterType: WafflesFilterType.number, testId: "exceptions-total" },
    fullMembershipCount: { displayText: "Membership Count", hidden: !!project?.editProjectOverrideMemberSets?.length, testId: "membership-count" },
    gapsFillable: { displayText: "Fillable", filterText: "Gaps: Fillable", filterType: WafflesFilterType.number, hidden: cmsCreditIsApplying || !userHasEnhancedFillableGaps, testId: "gaps-fillable" },
    gapsFillableByMultipleProviders: { displayText: "Fillable by > 1 Provider", filterText: "Gaps: Fillable", filterType: WafflesFilterType.number, hidden: cmsCreditIsApplying || !userHasEnhancedFillableGaps, testId: "gaps-fillable-by-multiple-providers", tooltip: "Count of the unique gaps that are fillable by adding a combination of more than 1 provider" },
    gapsFillableBySingleProvider: { displayText: "Fillable by Single Provider", filterText: "Gaps: Fillable", filterType: WafflesFilterType.number, hidden: cmsCreditIsApplying, testId: "gaps-fillable-by-single-provider" },
    gapsTotal: { displayText: "Total", filterText: "Gaps: Total", filterType: WafflesFilterType.number, testId: "gaps-total" },
    membershipRisk: { displayText: "#", filterText: "Membership At Risk #", filterType: WafflesFilterType.number, hidden: !userHasMembershipRisk, testId: "membership-risk" },
    membershipRiskPercent: { displayText: "%", filterText: "Membership At Risk %", filterType: WafflesFilterType.number, hidden: !userHasMembershipRisk, testId: "membership-risk-percent" },
    servicingMet: { displayText: "Met", filterText: "Servicing: Met", filterType: WafflesFilterType.number, testId: "servicing-met" },
    servicingNotMet: { displayText: "Not Met", filterText: "Servicing: Not Met", filterType: WafflesFilterType.number, testId: "servicing-not-met" },
    servicingTotal: { displayText: "Total", filterText: "Servicing: Total", filterType: WafflesFilterType.number, testId: "servicing-total" },
    title: { displayText: "Region", noFilter: true, sticky: true, testId: "title" },
  }), [project?.showAttestation, project?.editProjectOverrideMemberSets, cmsCreditIsApplying, userHasEnhancedFillableGaps, userHasMembershipRisk]);

  const data = useMemo(() => buildToppings(rows, matchUrl), [rows, matchUrl]);

  if (loading) {
    return <div className="loading-container">Loading...</div>;
  }

  if (error) {
    return <div className="error-container">Failed to load region data.</div>;
  }

  if (!rows.length) {
    return <div>No regions defined for this project.</div>;
  }

  let networkTypes = runConfiguration && Array.from(runConfiguration.networkTypes) || undefined;
  if (runConfiguration && !runConfiguration.useMarketProvidersForImpact) {
    networkTypes = networkTypes?.filter((nt) => nt !== "market");
  }

  const showNextClosestProviders = runConfiguration?.showNextClosestNetworkProviders || false;
  const showPatternOfCareCheckbox = showPatternOfCare(user, enabledFeatureFlags, !!compareToRunConfiguration, project, runConfiguration, adequacyResult?.aggregateDetails ?? [], undefined, cmsCreditIsApplying);
  const enableMembershipTypeOptions = runConfiguration && [ProjectRunConfigurationType.DualMembershipPrimary, ProjectRunConfigurationType.DualMembershipSupplemental].includes(runConfiguration.projectRunConfigurationType);
  const { primaryRunConfiguration, secondaryRunConfiguration } = getPrimaryAndSecondaryRunConfigurations(project.projectRunConfigurations, runConfiguration.projectRunConfigurationId);
  const selectedMemberships: number[] = [runConfiguration.memberSetId];
  if (isDualMembershipMode) {
    selectedMemberships.push(compareToRunConfiguration.memberSetId);
  }

  const bulkActionBar = (
    <span className="bulk-action-group">
      <Link<Partial<ExportDataRouteState> & ReportCustomizationRouteState>
        to={{
          pathname: exportEndpoint,
          state: {
            // old exportdata
            activeModule: module,
            adequacyFilters,
            availableExportTypes: [ExportType.RegionAggregate, ExportType.SpecialtySummaryByRegion, ExportType.ServicingProviders],
            comparedRunConfigurationId: exportComparedRunConfigurationId,
            disableRegionFilters: false,
            disableSpecialtyGroupFilters: false,
            enableMembershipTypeOptions,
            filters,
            granularity: RegionGranularity.State,
            initialNetworkAssessmentAnalysisType: networkAssessmentAnalysisType,
            networkTypes: networkTypes as ProviderNetworkTypeEnum[],
            nextClosestProviders: showNextClosestProviders,
            perspective,
            projectResourceId: project.resource.resourceId,
            projectResourceName: project.resource.name,
            projectURL: location.pathname + location.search,
            regionSetIds: [],
            showAdvancedComparison: project.showAdvancedComparison,
            showAppendNextClosestProviders: showNextClosestProviders,
            showAppendPatternOfCareProviders: showPatternOfCareCheckbox,
            showExpandableSpecialties: adequacyResult?.hasExpandableSpecialties ?? false,
            specialtyGroupCodes: [],

            // new reportcustomization
            /* oxlint-disable sort-keys */
            projectName: project.resource.name,
            networkAssessmentAnalysisType,
            showClosestProviderToMemberReport: project.showClosestProviderReport,
            selectedRegionSetIds: selectedKeys.length > 0
              ? selectedKeys.map((k) => parseInt(k))
              : rows.map((r) => r.regionSet.regionSetId).filter((id): id is number => id !== null),
            selectedSpecialties: [],
            defaultReportType: ReportType.AdequacyServicingProvider,
            primaryMembership: { alias: primaryRunConfiguration?.memberSetAlias || primaryRunConfiguration?.memberSetName, id: primaryRunConfiguration?.memberSetId },
            secondaryMembership: { alias: secondaryRunConfiguration?.memberSetAlias || secondaryRunConfiguration?.memberSetName, id: secondaryRunConfiguration?.memberSetId },
            selectedMemberships,
            selectedTimeslice: runConfiguration.projectRunConfigurationId,
            runFrequency: runFrequency || TimeFrequency.All,
            isCompareMode: !!compareToRunConfiguration && !isDualMembershipMode,
            selectedComparedTimeslice: !isDualMembershipMode ? compareToRunConfiguration?.projectRunConfigurationId : null,
            isViewingOpportunity: adequacyFilters?.opportunity,
            showAddedProviders: !!compareToRunConfiguration,
            showMatchedProviders: !!compareToRunConfiguration,
            showRemovedProviders: !!compareToRunConfiguration,
            showPatternOfCare: project.showPatternOfCareProviders,
            hasProspectProviders: !!runConfiguration.prospectProviderSetId,
            hasMarketProviders: !!runConfiguration.marketProviderSetId,
            showRegionSummaryReport: project.hasRegions,
            /* oxlint-enable */
          },
        }}
        data-testid="region-aggregate-view__create-report-button"
      >
        <i className="fa fa-cloud-download" /> Create Report
      </Link>
    </span>
  );

  return (
    <Waffles<RegionAggregateWaffleToppings>
      bulkActionBar={bulkActionBar}
      columns={columnConfig}
      columnGroups={COLUMN_GROUPS}
      customSort={customSort}
      data={data}
      fullWidth={true}
      handleCheckboxChange={setSelectedKeys}
      selectedKeys={selectedKeys}
      showCheckboxes={true}
      testId="region-aggregate-view"
    />
  );
};

export default RegionAggregateView;
